import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../../utils/dateLocale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import eventService from '../../services/eventService';
import { CreateTableReservationRequest, TableAvailability } from '../../types/calendar';
import { VenueDto, TimeSlotDto } from '../../services/eventService';
import { useFeedback } from '../../contexts/FeedbackContext';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';

interface TableReservationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialDate?: string;
}

const TableReservationDialog: React.FC<TableReservationDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialDate
}) => {
  const [formData, setFormData] = useState<CreateTableReservationRequest>({
    venueId: '',
    reservationDate: initialDate || new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    requiredTables: 1,
    notes: ''
  });
  const [venues, setVenues] = useState<VenueDto[]>([]);
  const [availability, setAvailability] = useState<TableAvailability | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const { showSuccess } = useFeedback();
  const confirm = useConfirm();
  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] A MEGNYITÁSKORI űrlap az alapvonal.
   * Lelet: `TableReservationDialog.tsx:250` (`<Dialog onClose={onClose}>`) — a háttér-kattintás/Esc
   * kérdés nélkül eldobta a kiválasztott helyszínt, időt, asztalszámot, megjegyzést.
   * `null` = zárva → soha nem piszkos.
   */
  const [baseline, setBaseline] = useState<CreateTableReservationRequest | null>(null);

  useEffect(() => {
    if (!open) {
      setBaseline(null);
    }
    if (open) {
      loadVenues();
      const initial: CreateTableReservationRequest = {
        venueId: '',
        reservationDate: initialDate || new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        requiredTables: 1,
        notes: ''
      };
      setFormData(initial);
      setBaseline(initial);
      setAvailability(null);
      setError('');
      setSuccessMessage('');
    }
  }, [open, initialDate]);

  // Auto-check availability when all required fields are filled
  useEffect(() => {
    if (!open || !formData.venueId || !formData.reservationDate || !formData.startTime || !formData.endTime || formData.requiredTables < 1) {
      return;
    }
    if (formData.startTime >= formData.endTime) {
      return;
    }

    const timer = setTimeout(() => {
      checkAvailability();
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.venueId, formData.reservationDate, formData.startTime, formData.endTime, formData.requiredTables]);

  const loadVenues = async () => {
    try {
      const response = await eventService.getVenues();
      const venueData = (response as any)?.data || response;

      if (Array.isArray(venueData)) {
        // Filter only active venues where table reservation is enabled
        setVenues(venueData.filter((venue: any) => venue.isActive && venue.tableReservationEnabled));
      } else {
        console.error('Venue data is not an array:', venueData);
        setError('Hiba történt a helyszínek betöltésekor');
        setVenues([]);
      }
    } catch (err) {
      console.error('Error loading venues:', err);
      // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
      // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
      setError(requestErrorMessage(err, 'Hiba történt a helyszínek betöltésekor'));
      setVenues([]);
    }
  };

  // Get the selected venue
  const selectedVenue = useMemo(() => {
    return venues.find(v => v.venueId === formData.venueId);
  }, [venues, formData.venueId]);

  // Check if current time is within allowed slots for the selected day
  const timeSlotWarning = useMemo(() => {
    if (!selectedVenue || !formData.startTime || !formData.endTime || !formData.reservationDate) {
      return null;
    }
    const slots = selectedVenue.tableReservationTimeSlots;
    if (!slots || slots.length === 0) return null;

    const dayOfWeek = new Date(formData.reservationDate + 'T00:00:00').getDay();
    const slotsForDay = slots.filter(s => s.dayOfWeek === undefined || s.dayOfWeek === dayOfWeek);

    if (slotsForDay.length === 0) {
      const dayNames = ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];
      return `Figyelem: Asztalfoglalás ezen a napon (${dayNames[dayOfWeek]}) nem engedélyezett`;
    }

    const withinSlot = slotsForDay.some(s => formData.startTime >= s.startTime && formData.endTime <= s.endTime);
    if (!withinSlot) {
      return `Figyelem: Asztalfoglalás csak a következő időszakokban engedélyezett: ${slotsForDay.map(s => `${s.startTime}-${s.endTime}`).join(', ')}`;
    }
    return null;
  }, [selectedVenue, formData.startTime, formData.endTime, formData.reservationDate]);

  const handleDateChange = (date: any) => {
    if (date) {
      const dateObj = date.$d || date;
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      setFormData(prev => ({
        ...prev,
        reservationDate: `${year}-${month}-${day}`
      }));
      setAvailability(null);
    }
  };

  const handleTimeChange = (field: 'startTime' | 'endTime', value: string) => {
    if (value) {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
      setAvailability(null);
    }
  };

  const checkAvailability = async () => {
    if (!formData.venueId || !formData.reservationDate || !formData.startTime || !formData.endTime) {
      setError('Kérjük töltse ki a kötelező mezőket');
      return;
    }

    if (formData.startTime >= formData.endTime) {
      setError('A kezdési időpontnak korábbinak kell lennie mint a befejezési időpont');
      return;
    }

    // Check if the selected time is in the past
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (formData.reservationDate === today) {
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      if (formData.startTime < currentTime) {
        setError('A kezdési időpont nem lehet a múltban');
        return;
      }
    }

    setCheckingAvailability(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await eventService.checkTableAvailability(
        formData.venueId,
        formData.reservationDate,
        formData.startTime,
        formData.endTime,
        formData.requiredTables
      );
      setAvailability(result);

      if (result.isAvailable) {
        setSuccessMessage(`✓ Szabad asztalok: ${result.availableTables}/${result.totalTables}`);
      } else {
        setSuccessMessage('');
        setError(result.message || `Nincs elég szabad asztal. Szabad: ${result.availableTables}/${result.totalTables}, Igényelt: ${formData.requiredTables}`);
      }
    } catch (err: any) {
      console.error('Error checking availability:', err);
      setError(requestErrorMessage(err, 'Hiba történt az elérhetőség ellenőrzésekor'));
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.venueId || !formData.reservationDate || !formData.startTime || !formData.endTime) {
      setError('Kérjük töltse ki a kötelező mezőket');
      return;
    }

    if (formData.startTime >= formData.endTime) {
      setError('A kezdési időpontnak korábbinak kell lennie mint a befejezési időpont');
      return;
    }

    if (!availability || !availability.isAvailable) {
      setError('Az asztalok nem elérhetőek a megadott időpontban');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await eventService.createTableReservation(formData);
      showSuccess('A foglalás leadva — jóváhagyás után véglegesítjük.');
      setBaseline(null);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating reservation:', err);
      // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat, nem a nyers 401
      setError(saveErrorMessage(err, 'Hiba történt az asztalfoglalás létrehozásakor'));
    } finally {
      setLoading(false);
    }
  };

  const parseDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const isDirty = open && isFormDirty(baseline, formData);
  useUnsavedChanges(isDirty, 'Asztalfoglalás');

  /** Háttér-kattintás, Esc és „Mégse”: beírt adat esetén a közös ablak kérdez; küldés közben nem zár. */
  const handleClose = async () => {
    if (loading) return;
    if (isDirty && !(await confirm(discardChangesConfirm('asztalfoglalás')))) return;
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu} localeText={HU_PICKER_LOCALE_TEXT}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Asztalfoglalás</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {!error && successMessage && <Alert severity="success">{successMessage}</Alert>}
            {!error && !successMessage && timeSlotWarning && <Alert severity="warning">{timeSlotWarning}</Alert>}

            {venues.length === 0 ? (
              <Alert severity="info">
                Nincs olyan helyszín ahol az asztalfoglalás engedélyezve lenne.
              </Alert>
            ) : (
              <FormControl fullWidth required>
                <InputLabel>Helyszín</InputLabel>
                <Select
                  value={formData.venueId}
                  label="Helyszín"
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, venueId: e.target.value }));
                    setAvailability(null);
                  }}
                >
                  {venues.map((venue) => (
                    <MenuItem key={venue.venueId} value={venue.venueId}>
                      <Box>
                        <Typography component="span">
                          {venue.name} ({venue.tableCount} asztal)
                        </Typography>
                        {venue.tableReservationTimeSlots && venue.tableReservationTimeSlots.length > 0 && (
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                            {(() => {
                              const dayNames = ['Vas', 'Hét', 'Kedd', 'Szer', 'Csüt', 'Pén', 'Szom'];
                              return '(' + venue.tableReservationTimeSlots!
                                .map(s => `${s.dayOfWeek !== undefined ? dayNames[s.dayOfWeek] + ' ' : ''}${s.startTime}-${s.endTime}`)
                                .join(', ') + ')';
                            })()}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <DatePicker
              label="Dátum"
              value={parseDate(formData.reservationDate)}
              onChange={handleDateChange}
              format="yyyy-MM-dd"
              minDate={new Date()}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true
                }
              }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Kezdés"
                type="time"
                value={formData.startTime}
                onChange={(e) => handleTimeChange('startTime', e.target.value)}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Befejezés"
                type="time"
                value={formData.endTime}
                onChange={(e) => handleTimeChange('endTime', e.target.value)}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="Asztalok száma"
              type="number"
              required
              fullWidth
              inputProps={{ min: 1, max: 99 }}
              value={formData.requiredTables || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setFormData(prev => ({ ...prev, requiredTables: 0 }));
                  setAvailability(null);
                } else {
                  const num = Number(value);
                  if (num >= 1 && num <= 99) {
                    setFormData(prev => ({ ...prev, requiredTables: num }));
                    setAvailability(null);
                  }
                }
              }}
            />

            <TextField
              label="Megjegyzés"
              fullWidth
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.notes?.length || 0}/500`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Mégse
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || checkingAvailability || !availability || !availability.isAvailable}
          >
            {loading ? <CircularProgress size={20} /> : checkingAvailability ? <CircularProgress size={20} /> : 'Foglalás leadása'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TableReservationDialog;
